'use client';

import React, { createContext, useContext, useReducer, useRef } from 'react';
import { VideoPlayerState, VideoPlayerAction, videoPlayerReducer } from './videoPlayerReducer';

interface VideoPlayerContextType {
  data: VideoPlayerState;
  actions: {
    play: () => void;
    pause: () => void;
    toggle: () => void;
    handleSeekMouseDown: () => void;
    handleSeekChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSeekMouseUp: (e: React.MouseEvent<HTMLInputElement>) => void;
    handleTimeUpdate: () => void;
    handleEnded: () => void;
  };
}

const VideoPlayerContext = createContext<VideoPlayerContextType | undefined>(undefined);

export const VideoPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const defaultVideoPlayerState: VideoPlayerState = {
    isPlaying: false,
    started: false,
    playerRef: useRef<HTMLVideoElement>(null),
    played: 0,
    seeking: false,
  };

  const [playerState, dispatch] = useReducer(videoPlayerReducer, defaultVideoPlayerState);

  const actions = {
    play: () => dispatch({ type: 'PLAY' }),
    pause: () => dispatch({ type: 'PAUSE' }),
    toggle: () => dispatch({ type: 'TOGGLE' }),
    handleSeekMouseDown: () => {
      dispatch({ type: 'SET_SEEKING', seeking: true });
    },
    handleSeekChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: 'SET_PLAYED', played: parseFloat(e.target.value) });
    },
    handleSeekMouseUp: (e: React.MouseEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      dispatch({ type: 'SEEK', seek: parseFloat(target.value) });
    },
    handleTimeUpdate: () => {
      dispatch({ type: 'UPDATE_PLAYED' });
    },
    handleEnded: () => {
      dispatch({ type: 'ENDED' });
    },
  };

  return (
    <VideoPlayerContext.Provider value={{ data: playerState, actions }}>
      {children}
    </VideoPlayerContext.Provider>
  );
};

export const useVideoPlayer = () => {
  const context = useContext(VideoPlayerContext);
  if (context === undefined) {
    throw new Error('useVideoPlayer must be used within a VideoPlayerProvider');
  }
  return context;
};